import { Link } from 'react-router-dom';

import styled, { css } from 'styled-components';

import fonts from 'theme/fonts';
import media from 'theme/media';

const getColor = (props) => {
    switch (props.variant) {
        case 'secondary':
            return props.disabled ? props.theme.purple_40_tr : props.theme.purple_100;
        case 'secondary_base':
            return props.disabled ? props.theme.purple_40_tr_base : props.theme.purple_100_base;
        case 'tertiary':
            return props.isInverted ? props.theme.white_base : props.disabled && !props.isInverted ? props.theme.grey_40_lc : props.theme.black;
        case 'error':
            return props.disabled ? props.theme.pink_40_tr : props.theme.pink_100;
        case 'row':
            return props.disabled ? props.theme.purple_40_tr : props.theme.purple_100;
        default:
            return props.disabled ? props.theme.disabled_button_color : props.theme.white_base;
    }
};

const getVisitedLinkColor = (props) => {
    switch (props.variant) {
        case 'secondary':
            return props.disabled ? props.theme.purple_40_tr : props.theme.purple_100;
        case 'secondary_base':
            return props.disabled ? props.theme.purple_40_tr_base : props.theme.purple_100_base;
        case 'tertiary':
            return props.isInverted ? props.theme.white_base : props.disabled && !props.isInverted ? props.theme.grey_40_lc : props.theme.black;
        case 'error':
            return props.disabled ? props.theme.pink_40_tr : props.theme.pink_100;
        case 'row':
            return props.disabled ? props.theme.purple_40_tr : props.theme.purple_100;
        default:
            return props.disabled ? props.theme.white_24_tr_base : props.theme.white_base;
    }
};

const getBackground = (props) => {
    if (props.variant === 'secondary_base') {
        return props.theme.white_base;
    }
    if (props.variant === 'primary' || !props.variant) {
        return props.disabled ? props.theme.purple_24_lc : props.theme.purple_100;
    }
    if (props.variant === 'danger') {
        return props.disabled ? props.theme.pink_40_lc : props.theme.pink_100;
    }
    if (props.isInverted) {
        return 'transparent';
    }
    return props.theme.white;
};

const getHoveredBackground = (props) => {
    switch (props.variant) {
        case 'secondary':
            return props.disabled ? props.theme.white : props.theme.purple_8_lc;
        case 'secondary_base':
            return props.disabled ? props.theme.white_base : props.theme.purple_8_lc_base;
        case 'tertiary':
            return props.isInverted ? 'transparent' : props.disabled && !props.isInverted ? props.theme.white : props.theme.grey_8_lc;
        case 'error':
            return props.disabled ? props.theme.white : props.theme.pink_8_tr;
        case 'danger':
            return props.disabled ? props.theme.pink_40_lc : props.theme.pink_72_lc;
        case 'row':
            return props.disabled ? props.theme.white : props.theme.purple_8_tr;
        default:
            return props.disabled ? props.theme.purple_24_lc : props.theme.purple_72_lc;
    }
};

const getBoxShadow = (props) => {
    const raised = props.raised && !props.disabled;
    if (props.variant === 'primary' || !props.variant) {
        return raised ? `0px 8px 8px ${props.theme.purple_16_tr}` : 'none';
    }
    if (props.variant === 'danger') {
        return raised ? `0 8px 24px ${props.theme.pink_16_tr}` : 'none';
    }
    return raised ? `${props.theme.shadow_grey}` : 'none';
};

const getMobilePadding = (props) => {
    if (props.variant === 'row') {
        return props.size === 'tight' ? '16px 24px' : '24px';
    }
    return props.size === 'tight' ? '8px 16px' : '16px 32px';
};
const getDesktopPadding = (props) => {
    if (props.variant === 'row') {
        return props.size === 'tight' ? '20px 32px' : '32px';
    }
    return props.size === 'tight' ? '12px 24px' : '20px 40px';
};
const getMobileRadius = (props) => {
    if (props.variant === 'row') {
        return '0';
    }
    return props.size === 'tight' ? '32px' : '56px';
};
const getDesktopRadius = (props) => {
    if (props.variant === 'row') {
        return '0';
    }
    return props.size === 'tight' ? '56px' : '72px';
};
const getMobileWidth = (props) => {
    return props.variant === 'row' && props.negativeMargin ? 'calc(100% + 48px)' : props.variant === 'row' ? '100%' : 'auto';
};
const getDesktopWidth = (props) => {
    return props.variant === 'row' && props.negativeMargin ? 'calc(100% + 64px)' : props.variant === 'row' ? '100%' : 'auto';
};

const CommonCss = css`
    font-family: ${fonts.regular};
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    border: none;
    width: ${(props) => getMobileWidth(props)};
    box-sizing: border-box;
    display: ${(props) => (props.variant === 'hidden' ? 'none' : 'inline-block')};
    outline: none;
    text-align: ${(props) => (props.variant === 'row' && !props.textCenter ? 'left' : 'center')};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    position: relative;
    vertical-align: top;
    cursor: ${(props) => (props.disabled ? 'default' : 'pointer')};
    pointer-events: auto;
    font-size: 17px;
    line-height: 24px;
    border-radius: ${(props) => getMobileRadius(props)};
    padding: ${(props) => getMobilePadding(props)};
    color: ${(props) => getColor(props)};
    background: ${(props) => getBackground(props)};
    box-shadow: ${(props) => getBoxShadow(props)};
    visibility: ${(props) => (props.hidden ? 'hidden' : 'visible')};
    margin: ${(props) => (props.negativeMargin ? '0 -24px' : null)};

    ${media.desktop1920} {
        font-size: 20px;
        line-height: 32px;
        border-radius: ${(props) => getDesktopRadius(props)};
        padding: ${(props) => getDesktopPadding(props)};
        margin: ${(props) => (props.negativeMargin ? '0 -32px' : null)};
        width: ${(props) => getDesktopWidth(props)};
    }

    &:link,
    &:visited,
    &:focus {
        color: ${(props) => getVisitedLinkColor(props)};
    }

    &:hover,
    &:active,
    &:focus {
        background: ${(props) => getHoveredBackground(props)};
        color: ${(props) => (props.variant === 'tertiary' && props.isInverted ? props.theme.white_40_tr_base : null)};
    }
`;

export const CommonButton = styled.button`
    ${CommonCss}
`;

export const LinkButton = styled.a`
    ${CommonCss}
`;

export const ReactLinkButton = styled(Link)`
    ${CommonCss}
`;
